import { z } from "zod";
/**
 * Environment shared by every server process and dev script.
 * Apps extend it with their own variables. Documented in `.env.example`.
 */
const postgresUrl = z.url({ protocol: /^postgres(ql)?$/ });
export const envSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "staging", "production"]),
    /** Runtime connection as `app_user` (no DDL, no BYPASSRLS). */
    DATABASE_URL: postgresUrl,
    REDIS_URL: z.url({ protocol: /^rediss?$/ }),
    MEILI_URL: z.url({ protocol: /^https?$/ }),
    MEILI_MASTER_KEY: z.string().min(16),
    S3_ENDPOINT: z.url({ protocol: /^https?$/ }),
    S3_REGION: z.string().min(1),
    S3_BUCKET: z.string().min(3),
    S3_ACCESS_KEY_ID: z.string().min(1),
    S3_SECRET_ACCESS_KEY: z.string().min(1),
    SMTP_HOST: z.string().min(1),
    SMTP_PORT: z.coerce.number().int().min(1).max(65_535),
});
// `.env` files write an unset optional value as `KEY=`; treat the empty string as absent.
const optionalUrl = z
    .union([z.literal(""), z.url({ protocol: /^https?$/ })])
    .optional()
    .transform((value) => value || undefined);
const optionalString = z
    .string()
    .optional()
    .transform((value) => value || undefined);
/** 32 bytes as standard base64 (44 characters). Kept as a string; the API decodes it. */
const key32 = z
    .string()
    .refine((value) => /^[A-Za-z0-9+/]{43}=$/.test(value), "must be 32 bytes, base64-encoded");
/** `https://host[:port]` with nothing after it (lower-case, no path or trailing slash): what a browser sends in `Origin`. */
// `tauri://localhost` is the POS webview's origin on macOS and Linux (Windows uses `http://tauri.localhost`).
const isOrigin = (value) => value === "tauri://localhost" ||
    /^https?:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/.test(value);
/** `apps/api` and `apps/worker` (B-05). */
export const apiEnvSchema = envSchema.extend({
    HOST: z.string().min(1).default("0.0.0.0"),
    PORT: z.coerce.number().int().min(1).max(65_535).default(3000),
    DATABASE_POOL_MAX: z.coerce.number().int().min(1).max(100).default(10),
    /**
     * HD-02: connection as `platform_user` (Super Admin requests, platform jobs, sign-up). Unset in development and
     * tests: `DATABASE_URL` with the user switched; production and staging must set it.
     */
    DATABASE_PLATFORM_URL: z
        .union([z.literal(""), postgresUrl])
        .optional()
        .transform((value) => value || undefined),
    /** HD-07: every job runs on this pool, so it holds the sum of the queues' concurrency (the worker checks at start). */
    DATABASE_PLATFORM_POOL_MAX: z.coerce.number().int().min(1).max(100).default(24),
    /** HD-07: how long a request waits for a free connection before answering 503 (a flash sale queues here). */
    DATABASE_POOL_WAIT_MS: z.coerce.number().int().min(100).max(60_000).default(15_000),
    LOG_LEVEL: z.enum(["fatal", "error", "warn", "info", "debug", "trace", "silent"]).default("info"),
    SENTRY_DSN: optionalUrl,
    SENTRY_ENVIRONMENT: optionalString,
    SENTRY_TRACES_SAMPLE_RATE: z.coerce.number().min(0).max(1).default(0),
    /** AES-256-GCM key for stored secrets (`core/crypto`, CLAUDE.md §8). Env only, never in the database. */
    ENCRYPTION_KEY: key32,
    /** Ed25519 private key for access tokens: PKCS#8 DER, base64 (docs/specs/identity.md, ADR-0011). */
    JWT_PRIVATE_KEY: z.string().refine((value) => /^[A-Za-z0-9+/]+={0,2}$/.test(value), "must be base64"),
    /** `kid` of that key; tokens with another `kid` are rejected (room for rotation). */
    JWT_KEY_ID: z.string().min(1).max(64),
    JWT_ISSUER: z.string().min(1).default("commerce-os"),
    JWT_AUDIENCE: z.string().min(1).default("commerce-os-api"),
    /** HMAC key for one-time codes kept in Redis (OTPs are stored as HMACs, never in clear). */
    AUTH_HMAC_KEY: key32,
    /** Admin app origin, for links in e-mails (password reset). */
    ADMIN_URL: z.url({ protocol: /^https?$/ }).default("http://localhost:5173"),
    /**
     * Shop address of every storefront (docs/specs/storefront.md): `{slug}` is replaced by the tenant's slug. The host
     * part after `{slug}.` is the platform storefront domain (PRD SF-01); the owner has not chosen it yet, so it is
     * config only.
     */
    STOREFRONT_URL_TEMPLATE: z
        .string()
        .default("http://{slug}.localhost:3100")
        .refine((value) => /^https?:\/\/\{slug\}\.[a-z0-9.-]+(:\d{1,5})?$/.test(value), "must look like https://{slug}.shops.example.com"),
    /**
     * Own shop domains through Cloudflare for SaaS (B-73, docs/specs/storefront.md): `fake` turns every domain active
     * without calling Cloudflare (development, tests), `live` calls the custom hostnames API. Defaults to `live` in
     * production only.
     */
    CUSTOM_HOSTNAMES_MODE: z
        .union([z.literal(""), z.enum(["fake", "live"])])
        .optional()
        .transform((value) => value || undefined),
    /** Cloudflare API token with "SSL and Certificates: Edit" on the storefront zone, and that zone's ID. */
    CLOUDFLARE_API_TOKEN: optionalString,
    CLOUDFLARE_ZONE_ID: optionalString,
    /**
     * Public images of the online store (B-74): a second bucket, readable by anyone, so the private bucket never is
     * (R2 public access is per bucket). Unset: shops show no images.
     */
    S3_PUBLIC_BUCKET: optionalString,
    /** That bucket's public base URL (an R2 custom domain behind the CDN), e.g. `https://images.<domain>`. */
    PUBLIC_FILES_URL: optionalUrl,
    /**
     * Bot check on the storefront's OTP and order requests (B-76): `fake` accepts any answer except "fail"
     * (development, tests), `live` asks Cloudflare Turnstile with `TURNSTILE_SECRET_KEY`. Defaults to `live` in
     * production only.
     */
    TURNSTILE_MODE: z
        .union([z.literal(""), z.enum(["fake", "live"])])
        .optional()
        .transform((value) => value || undefined),
    TURNSTILE_SECRET_KEY: optionalString,
    /**
     * DG-02: Google's public keys as a JSON Web Key Set, instead of fetching them from Google — tests only (they sign
     * their own ID tokens). Refused in staging and production.
     */
    GOOGLE_JWKS_JSON: optionalString,
    /**
     * Online payments of the storefront (B-77): `fake` pays every checkout at once without calling a gateway
     * (development, tests), `live` calls bKash, Nagad and SSLCommerz. Defaults to `live` in production only.
     */
    STOREFRONT_GATEWAYS_MODE: z
        .union([z.literal(""), z.enum(["fake", "live"])])
        .optional()
        .transform((value) => value || undefined),
    /**
     * B-82: Meta Conversions API calls — `fake` records them without calling Meta (development, tests), `live` sends
     * them. Defaults to `live` in production only.
     */
    STOREFRONT_TRACKING_MODE: z
        .union([z.literal(""), z.enum(["fake", "live"])])
        .optional()
        .transform((value) => value || undefined),
    /**
     * B-83: the storefront server as the worker reaches it, and the shared secret, for "this shop changed" calls to its
     * `/api/revalidate`. Unset → no calls (the storefront's 60-second cache lifetime still applies).
     */
    STOREFRONT_REVALIDATE_URL: z
        .union([z.literal(""), z.url({ protocol: /^https?$/ })])
        .optional()
        .transform((value) => value || undefined),
    STOREFRONT_REVALIDATE_SECRET: optionalString,
    /** Where a merchant's CNAME points (the Cloudflare for SaaS fallback origin); default `customers.<platform domain>`. */
    STOREFRONT_CNAME_TARGET: optionalString,
    /** Worker: how often the outbox dispatcher looks for new events (docs/specs/events-jobs.md). */
    OUTBOX_POLL_MS: z.coerce.number().int().min(50).max(60_000).default(1000),
    /** Worker: jobs processed in parallel per queue. */
    WORKER_CONCURRENCY: z.coerce.number().int().min(1).max(100).default(5),
    /**
     * Who may receive SMS/e-mail (docs/specs/notifications.md): `development` (log + Mailpit only), `allowlist`
     * (only `NOTIFY_ALLOWLIST`), `live` (everyone; production only). Defaults from `NODE_ENV`.
     */
    NOTIFY_MODE: z
        .union([z.literal(""), z.enum(["development", "allowlist", "live"])])
        .optional()
        .transform((value) => value || undefined),
    /** Comma-separated E.164 numbers and e-mail addresses that may receive messages in `allowlist` mode. */
    NOTIFY_ALLOWLIST: optionalString,
    /**
     * Courier adapters (docs/specs/shipping.md): `fake` books with a built-in stand-in that never calls a courier,
     * `live` calls the real courier APIs. Defaults to `live` in production only.
     */
    COURIER_MODE: z
        .union([z.literal(""), z.enum(["fake", "live"])])
        .optional()
        .transform((value) => value || undefined),
    /** SMS adapter. `log` writes (masked) to the log; `memory` is for tests. Real providers are added as adapters. */
    SMS_PROVIDER: z.enum(["log", "memory"]).default("log"),
    /** E-mail adapter: `smtp` (Mailpit locally, any SMTP service elsewhere) or `memory` for tests. */
    EMAIL_PROVIDER: z.enum(["smtp", "memory"]).default("smtp"),
    EMAIL_FROM: z.string().min(3).default("Sobthik <no-reply@localhost>"),
    /**
     * Serve `/docs` and `/docs/openapi.json` (docs/specs/contracts-api-client.md). Defaults to on in development
     * and staging; never allowed in production.
     */
    API_DOCS: z
        .union([z.literal(""), z.enum(["true", "false"])])
        .optional()
        .transform((value) => (value === "true" ? true : value === "false" ? false : undefined)),
    /**
     * Exact browser origins allowed to call the API with credentials, comma-separated
     * (`https://admin.example.com`; ADR-0014). Empty: no CORS at all (same origin, e.g. the Vite dev proxy).
     */
    CORS_ORIGINS: z
        .string()
        .optional()
        .transform((value, ctx) => {
        const origins = (value ?? "")
            .split(",")
            .map((origin) => origin.trim())
            .filter((origin) => origin !== "");
        for (const origin of origins) {
            if (!isOrigin(origin)) {
                ctx.addIssue({
                    code: "custom",
                    message: `"${origin}" is not an origin like https://admin.example.com`,
                });
            }
        }
        return origins;
    }),
    /** Meilisearch as browsers reach it (Cloudflare-proxied outside dev); defaults to `MEILI_URL` (docs/specs/search.md). */
    MEILI_PUBLIC_URL: optionalUrl,
    /** SMTP login, when the SMTP service needs one (production providers do; Mailpit does not). */
    SMTP_USER: optionalString,
    SMTP_PASSWORD: optionalString,
    /**
     * `saas` (many businesses, public signup), `dedicated` (one business on its own servers, PRD §18,
     * docs/specs/dedicated.md: signup off, billing replaced by the licence, Super Admin optional) or `sandbox` (the
     * "Try it yourself" demo, docs/specs/sandbox.md: throw-away businesses, every outside call a stand-in, nothing
     * that takes money or reaches a person).
     */
    DEPLOYMENT_MODE: z.enum(["saas", "dedicated", "sandbox"]).default("saas"),
    /** Sandbox (docs/specs/sandbox.md): ready demo businesses kept in the pool. */
    SANDBOX_POOL_SIZE: z.coerce.number().int().min(1).max(200).default(10),
    /** Sandbox: demo businesses claimed and not yet expired at once; more → 503 SANDBOX_BUSY. */
    SANDBOX_MAX_ACTIVE: z.coerce.number().int().min(1).max(1000).default(100),
    /** Sandbox: demos one IP address may start per day. */
    SANDBOX_PER_IP_DAILY: z.coerce.number().int().min(1).max(100).default(10),
    /** Sandbox: the plan every demo business is on (all features visible); a plan key, so plans stay data. */
    SANDBOX_PLAN_KEY: z.string().trim().min(1).default("business"),
    /**
     * Sandbox: the API as the worker reaches it (internal network): the demo template builds each business through it,
     * as its Owner and its POS would (SB-03b).
     */
    SANDBOX_API_URL: z.url({ protocol: /^https?$/ }).default("http://127.0.0.1:3000"),
    /** Sandbox: days of POS history each demo business starts with (0 = today only, for fast tests). */
    SANDBOX_HISTORY_DAYS: z.coerce.number().int().min(0).max(60).default(30),
    /** Sandbox: `off` builds demo businesses without pictures (tests; a sandbox without internet). */
    SANDBOX_DEMO_PHOTOS: z.enum(["on", "off"]).default("on"),
    /**
     * ST-25 (docs/specs/theme-demos.md): the API as the worker reaches it (internal network); the theme demo shops are
     * filled through it, as their Owner would.
     */
    DEMO_SHOPS_API_URL: z.url({ protocol: /^https?$/ }).default("http://127.0.0.1:3000"),
    /** ST-25: `off` builds the theme demo shops without pictures (tests). */
    DEMO_SHOPS_PHOTOS: z.enum(["on", "off"]).default("on"),
    /** Sandbox: the browser POS address (SB-04); unset → the claim answers `posUrl: null`. */
    SANDBOX_POS_URL: optionalUrl,
    /** Serve the Super Admin API (`/v1/platform/...`). Defaults to on in `saas`, off in `dedicated`. */
    SUPER_ADMIN_ENABLED: z
        .union([z.literal(""), z.enum(["true", "false"])])
        .optional()
        .transform((value) => (value === "true" ? true : value === "false" ? false : undefined)),
    /**
     * Proxies in front of the API whose `X-Forwarded-For` hop is trusted (B-67): 2 behind Cloudflare + Coolify, 1
     * behind the dedicated Caddy, 0 locally. Wrong values make every client share one IP for rate limits.
     */
    TRUST_PROXY_HOPS: z.coerce.number().int().min(0).max(5).default(0),
    /**
     * Operations alerts (B-68, docs/runbooks/alerts.md): a Telegram bot and chat for the owner's phone, and/or e-mail
     * addresses (comma-separated). Neither set → alerts are only logged.
     */
    ALERT_TELEGRAM_BOT_TOKEN: optionalString,
    ALERT_TELEGRAM_CHAT_ID: optionalString,
    ALERT_EMAILS: optionalString,
    /** Telegram's API base (tests point it at a local server). */
    ALERT_TELEGRAM_API_URL: z.url({ protocol: /^https?$/ }).default("https://api.telegram.org"),
    /** Disk use (percent) of the server's root filesystem that raises an alert. */
    ALERT_DISK_PERCENT: z.coerce.number().int().min(50).max(99).default(80),
    /** Dedicated mode: the signed licence (`col1.<payload>.<signature>`), or `LICENCE_FILE` with its path. */
    LICENCE: optionalString,
    LICENCE_FILE: optionalString,
});
/** `pnpm db:migrate`. Separate from the API env: the API never holds migrator credentials. */
export const migratorEnvSchema = z.object({
    NODE_ENV: envSchema.shape.NODE_ENV,
    /** Connection as `app_migrator`, which owns the schema and runs migrations. */
    DATABASE_MIGRATOR_URL: postgresUrl,
});
/** `pnpm db:roles`: a superuser connection, plus the role URLs whose user and password it applies. */
export const dbRolesEnvSchema = migratorEnvSchema.extend({
    DATABASE_ADMIN_URL: postgresUrl,
    DATABASE_URL: postgresUrl,
    DATABASE_PLATFORM_URL: apiEnvSchema.shape.DATABASE_PLATFORM_URL,
});
/** Lists the variables that failed validation. Never carries the values themselves. */
export class EnvValidationError extends Error {
    issues;
    constructor(issues) {
        super(`Invalid environment:\n${issues.map((i) => `  - ${i.variable}: ${i.problem}`).join("\n")}`);
        this.name = "EnvValidationError";
        this.issues = issues;
    }
    get variables() {
        return [...new Set(this.issues.map((i) => i.variable))];
    }
}
// Built from the issue code only: Zod messages can echo the received input, which may be a secret.
function describeIssue(issue) {
    switch (issue.code) {
        case "invalid_type":
            return issue.input === undefined ? "is required" : `must be a ${issue.expected}`;
        case "invalid_value":
            return `must be one of: ${issue.values.map(String).join(", ")}`;
        case "invalid_format":
            return `must be a valid ${issue.format}`;
        case "too_small":
            return issue.origin === "string"
                ? `must have at least ${String(issue.minimum)} characters`
                : `must be at least ${String(issue.minimum)}`;
        case "too_big":
            return `must be at most ${String(issue.maximum)}`;
        // Our own `refine` messages, which never include the input.
        case "custom":
            return issue.message;
        default:
            return "is invalid";
    }
}
function parseEnv(schema, source) {
    const result = schema.safeParse(source);
    if (result.success)
        return result.data;
    throw new EnvValidationError(result.error.issues.map((issue) => ({
        variable: issue.path.map(String).join(".") || "(root)",
        problem: describeIssue(issue),
    })));
}
/** Validates `source` (usually `process.env`) and returns typed values, or throws `EnvValidationError`. */
export function loadEnv(source) {
    return parseEnv(envSchema, source);
}
/**
 * The secrets of `.env.example`: published in the repository, so only ever valid for development. Keep in step with
 * that file (apps/api/src/core/config/development-secrets.test.ts reads it).
 */
export const DEVELOPMENT_SECRETS = [
    "ZGV2LW9ubHktZW5jcnlwdGlvbi1rZXktMzItYnl0ZXM=",
    "ZGV2LW9ubHktb3RwLWhtYWMta2V5LTMyLWJ5dGVzISE=",
    "MC4CAQAwBQYDK2VwBCIEIP48TpPLxVdW+VmH6GlbP8b1AzxEF5n0VyrOT6Kze6nJ",
];
/**
 * Same as `loadEnv`, plus the API/worker variables. Staging and production refuse the development secrets of
 * `.env.example` (B-67, docs/security/asvs.md V6.4): with the public JWT key anyone could sign in as anyone.
 */
/**
 * SB-02: a sandbox never calls a real courier, gateway, Cloudflare or Meta, never messages a person and never serves
 * the Super Admin. Each adapter must be set to its stand-in explicitly: unset would mean `live` in production.
 * `NOTIFY_MODE=allowlist` with an empty list keeps every message as "suppressed" (`development` would still send
 * e-mail through SMTP).
 */
function sandboxIssues(env) {
    const issues = [];
    const fakes = {
        COURIER_MODE: env.COURIER_MODE,
        STOREFRONT_GATEWAYS_MODE: env.STOREFRONT_GATEWAYS_MODE,
        CUSTOM_HOSTNAMES_MODE: env.CUSTOM_HOSTNAMES_MODE,
        STOREFRONT_TRACKING_MODE: env.STOREFRONT_TRACKING_MODE,
    };
    for (const [variable, value] of Object.entries(fakes)) {
        if (value !== "fake")
            issues.push({ variable, problem: "must be fake on a sandbox" });
    }
    if (env.NOTIFY_MODE !== "allowlist")
        issues.push({ variable: "NOTIFY_MODE", problem: "must be allowlist (with an empty list) on a sandbox" });
    if (env.NOTIFY_ALLOWLIST !== undefined)
        issues.push({
            variable: "NOTIFY_ALLOWLIST",
            problem: "must be empty on a sandbox: nobody receives messages",
        });
    if (env.SUPER_ADMIN_ENABLED === true)
        issues.push({ variable: "SUPER_ADMIN_ENABLED", problem: "is not allowed on a sandbox" });
    return issues;
}
export function loadApiEnv(source) {
    const env = parseEnv(apiEnvSchema, source);
    if (env.DEPLOYMENT_MODE === "sandbox") {
        const issues = sandboxIssues(env);
        if (issues.length > 0)
            throw new EnvValidationError(issues);
    }
    if (env.NODE_ENV === "production" || env.NODE_ENV === "staging") {
        const leaked = ["ENCRYPTION_KEY", "AUTH_HMAC_KEY", "JWT_PRIVATE_KEY"].filter((name) => DEVELOPMENT_SECRETS.includes(env[name]));
        if (leaked.length > 0)
            throw new EnvValidationError(leaked.map((variable) => ({ variable, problem: "is a development value: generate a new one" })));
        if (env.DATABASE_PLATFORM_URL === undefined)
            throw new EnvValidationError([
                { variable: "DATABASE_PLATFORM_URL", problem: "is required here (platform_user, its own password)" },
            ]);
        // Any other user connects fine and then every platform write answers "permission denied" (found in production).
        if (!/^postgres(ql)?:\/\/platform_user[:@]/.test(env.DATABASE_PLATFORM_URL))
            throw new EnvValidationError([
                { variable: "DATABASE_PLATFORM_URL", problem: "must connect as platform_user" },
            ]);
        if (env.GOOGLE_JWKS_JSON !== undefined)
            throw new EnvValidationError([
                { variable: "GOOGLE_JWKS_JSON", problem: "is for tests only: Google's own keys are used here" },
            ]);
    }
    return env;
}
export function loadMigratorEnv(source) {
    return parseEnv(migratorEnvSchema, source);
}
export function loadDbRolesEnv(source) {
    return parseEnv(dbRolesEnvSchema, source);
}
/** `apps/storefront` (docs/specs/storefront.md): the Next.js server only talks to the API. */
export const storefrontEnvSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "staging", "production"]).default("development"),
    /** The API as the storefront server reaches it (internal network in production). */
    STOREFRONT_API_URL: z.url({ protocol: /^https?$/ }).default("http://localhost:3000"),
    /** Cloudflare Turnstile site key for the checkout's bot check (public); unset → the development stand-in. */
    TURNSTILE_SITE_KEY: optionalString,
    /** B-83: shared with the API; it proves the call to `/api/revalidate`. Unset → the route answers 404. */
    STOREFRONT_REVALIDATE_SECRET: optionalString,
    /** ST-12: the Admin app; only it may frame a draft preview (its live preview panel). */
    STOREFRONT_ADMIN_URL: z.url({ protocol: /^https?$/ }).default("http://localhost:5173"),
});
export function loadStorefrontEnv(source) {
    return parseEnv(storefrontEnvSchema, source);
}
/**
 * The public marketing site (apps/web, docs/specs/marketing-site.md). Read when the site is built (static pages), so a
 * change needs a rebuild. Brand and links are configuration, not code (decision 5).
 */
export const webEnvSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "staging", "production"]).default("development"),
    /** The product's name as the site shows it. */
    WEB_BRAND_NAME: z.string().trim().min(2).max(40).default("Sobthik"),
    /** The site's own address (canonical links, sitemap). */
    WEB_SITE_URL: z.url({ protocol: /^https?$/ }).default("http://localhost:3200"),
    /** The Admin, where "Sign in" and "Start free trial" go. */
    WEB_ADMIN_URL: z.url({ protocol: /^https?$/ }).default("http://localhost:5173"),
    /** Sales WhatsApp number, digits only with country code (`8801XXXXXXXXX`); unset → no WhatsApp button. */
    WEB_WHATSAPP: z
        .union([z.literal(""), z.string().regex(/^8801\d{9}$/)])
        .optional()
        .transform((value) => value || undefined),
    /** Sales phone in E.164 (`+8801XXXXXXXXX`); unset → no phone link. */
    WEB_PHONE: z
        .union([z.literal(""), z.string().regex(/^\+8801\d{9}$/)])
        .optional()
        .transform((value) => value || undefined),
    /**
     * MK-06: the production API — the pricing page reads `/v1/public/plans` (when built, then every 10 minutes) and the
     * enquiry form posts `/v1/public/leads`. Unset → prices "on request" and no form.
     */
    WEB_API_URL: z
        .union([z.literal(""), z.url({ protocol: /^https?$/ })])
        .optional()
        .transform((value) => value || undefined),
    /**
     * MK-07: address of a theme's live demo shop, `{slug}` → `demo-<theme>` (ST-24), e.g. `https://{slug}.<domain>`;
     * unset → the pages show the screenshot without a "visit the live shop" link.
     */
    WEB_DEMO_SHOP_URL_TEMPLATE: z
        .union([z.literal(""), z.string().regex(/^https?:\/\/[^\s]*\{slug\}[^\s]*$/)])
        .optional()
        .transform((value) => value || undefined),
    /** SB-07: the sandbox API "Try it yourself" claims from (`https://api.<demo domain>`); unset → the demo page waits. */
    WEB_SANDBOX_API_URL: z
        .union([z.literal(""), z.url({ protocol: /^https?$/ })])
        .optional()
        .transform((value) => value || undefined),
    /** SB-07: Cloudflare Turnstile site key for the demo page; unset → the sandbox's stand-in check (development). */
    WEB_TURNSTILE_SITE_KEY: z
        .union([z.literal(""), z.string().min(1)])
        .optional()
        .transform((value) => value || undefined),
});
export function loadWebEnv(source) {
    return parseEnv(webEnvSchema, source);
}
//# sourceMappingURL=env.js.map